import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

const readCookie = (name) => document.cookie.split("; ").find(c => c.startsWith(`${name}=`))?.split("=").slice(1).join("=") || "";

api.interceptors.request.use((cfg) => {
  // Session rides in an httpOnly cookie; writes echo the readable csrf_token cookie (double-submit).
  if (!["get", "head", "options"].includes((cfg.method || "get").toLowerCase())) {
    const csrf = readCookie("csrf_token");
    if (csrf) cfg.headers["X-CSRF-Token"] = decodeURIComponent(csrf);
  }
  const legacy = localStorage.getItem("access_token");
  if (legacy) cfg.headers.Authorization = `Bearer ${legacy}`; // 14-day transition: converted to a cookie on first /auth/me
  return cfg;
});

// View-as sessions: the server refuses every write with this exact message — surface it once per attempt.
let _lastViewOnlyToast = 0;
api.interceptors.response.use(undefined, (err) => {
  const detail = err?.response?.data?.detail;
  const status = err?.response?.status;
  if (status === 401 && typeof detail === "string" && detail.includes("expired") && !window.location.pathname.startsWith("/login")) {
    localStorage.removeItem("access_token");
    window.location.href = "/login?expired=1";
  }
  if (status === 403 && typeof detail === "string" && detail.startsWith("View-only session")) {
    const now = Date.now();
    if (now - _lastViewOnlyToast > 1500) {
      _lastViewOnlyToast = now;
      import("sonner").then(({ toast }) => toast.warning("View-only session — nothing is saved.", { id: "view-only" }));
    }
  }
  return Promise.reject(err);
});

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    // Pydantic 422 errors: {loc:[body, rowIdx, field], msg:...}
    const parts = detail.map((e) => {
      if (!e || typeof e !== "object") return JSON.stringify(e);
      const loc = Array.isArray(e.loc) ? e.loc : [];
      const rowIdx = loc.find((x) => typeof x === "number");
      const field = [...loc].reverse().find((x) => typeof x === "string" && x !== "body");
      const where =
        rowIdx !== undefined && field ? `Row ${rowIdx + 1} · ${field}: `
        : field ? `${field}: `
        : "";
      return where + (typeof e.msg === "string" ? e.msg : JSON.stringify(e));
    });
    return parts.filter(Boolean).slice(0, 4).join(" · ") + (parts.length > 4 ? ` (+${parts.length - 4} more)` : "");
  }
  // Rich structured errors — e.g. the slot-clash 409 that carries team + coach
  // contact. Callers that want the full object should inspect
  // err.response.data.detail directly; here we return a friendly short message.
  if (detail && typeof detail === "object" && detail.code === "slot_clash") {
    const who = detail.team_name || detail.user_name || "another coach";
    const where = detail.pitch_name ? `${detail.venue_short_code ? `[${detail.venue_short_code}] ` : ""}${detail.pitch_name}` : "this pitch";
    if (detail.team_clash) return `${detail.team_clash_team || who} already has a booking that day: ${detail.start}–${detail.end} on ${where}`;
    return `${detail.start}–${detail.end} on ${where} is already taken by ${who}`;
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
